import React, { useState, useCallback } from 'react';
import StarField from './components/StarField';
import RatingButton from './components/RatingButton';
import { RatingOption, RatingValue, SurveyState } from './types';

const RATING_OPTIONS: RatingOption[] = [
  {
    value: 'good',
    label: 'Good',
    emoji: '😊',
    description: 'Positive experience',
    color: 'text-emerald-400',
    glowColor: 'rgba(52,211,153,0.7)',
    borderColor: 'border-emerald-400/60',
    bgGradient: 'linear-gradient(135deg, rgba(52,211,153,0.12), rgba(16,185,129,0.08))',
    particleColor: '#34d399',
  },
  {
    value: 'neutral',
    label: 'Okay',
    emoji: '😐',
    description: 'Average experience',
    color: 'text-amber-400',
    glowColor: 'rgba(251,191,36,0.7)',
    borderColor: 'border-amber-400/60',
    bgGradient: 'linear-gradient(135deg, rgba(251,191,36,0.12), rgba(245,158,11,0.08))',
    particleColor: '#fbbf24',
  },
  {
    value: 'poor',
    label: 'Poor',
    emoji: '😞',
    description: 'Negative experience',
    color: 'text-rose-400',
    glowColor: 'rgba(251,113,133,0.7)',
    borderColor: 'border-rose-400/60',
    bgGradient: 'linear-gradient(135deg, rgba(251,113,133,0.12), rgba(244,63,94,0.08))',
    particleColor: '#fb7185',
  },
];

const INITIAL_STATE: SurveyState = {
  step: 'rating',
  rating: null,
  comment: '',
};

function App() {
  const [state, setState] = useState<SurveyState>(INITIAL_STATE);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  const handleRatingSelect = useCallback((value: RatingValue) => {
    setState(prev => ({ ...prev, rating: value }));
  }, []);

  const handleNext = useCallback(() => {
    if (!state.rating) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setState(prev => ({ ...prev, step: 'comment' }));
      setIsTransitioning(false);
    }, 300);
  }, [state.rating]);

  // Submit Feedback -> opens popup
  const handleSubmit = useCallback(() => {
    setShowPopup(true);
  }, []);

  // Skip -> clears comment and opens popup
  const handleSkip = useCallback(() => {
    setState(prev => ({ ...prev, comment: '' }));
    setShowPopup(true);
  }, []);

  const handleClosePopup = useCallback(() => {
    setShowPopup(false);
  }, []);

  const handleReset = useCallback(() => {
    setShowPopup(false);
    setIsTransitioning(true);
    setTimeout(() => {
      setState(INITIAL_STATE);
      setIsTransitioning(false);
    }, 300);
  }, []);

  const selectedOption = RATING_OPTIONS.find(o => o.value === state.rating);

  return (
    <div className="min-h-screen cosmic-grid flex flex-col items-center justify-center p-4 sm:p-6 lg:p-8 relative">
      <StarField />

      <div
        className={`relative w-full max-w-lg glass-card rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 shadow-2xl transition-all duration-300
          ${isTransitioning ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}
        style={{ boxShadow: '0 0 60px rgba(139,92,246,0.1), 0 25px 50px rgba(0,0,0,0.5)' }}
        role="main"
      >
        <div
          className="absolute top-0 left-8 right-8 h-0.5 rounded-full opacity-60"
          style={{ background: 'linear-gradient(90deg, transparent, #a78bfa, #f472b6, #a78bfa, transparent)' }}
        />

        {state.step === 'rating' && (
          <div className="space-y-6 sm:space-y-8">
            <div className="text-center space-y-2" style={{ animation: 'slideUp 0.5s ease-out' }}>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-500/20 mb-3">
                <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />
                <span className="text-violet-400 text-xs font-display font-semibold uppercase tracking-widest">
                  Customer Feedback
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-display text-white leading-tight">
                How was your
                <br />
                <span className="shimmer-text">experience today?</span>
              </h1>
              <p className="text-slate-400 text-sm sm:text-base mt-2">
                Tap a rating below — your feedback matters to us
              </p>
            </div>

            <div className="grid grid-cols-3 gap-3 sm:gap-4">
              {RATING_OPTIONS.map((option, i) => (
                <RatingButton
                  key={option.value}
                  option={option}
                  selected={state.rating === option.value}
                  onSelect={handleRatingSelect}
                  index={i}
                />
              ))}
            </div>

            <div style={{ animation: 'slideUp 0.5s ease-out 0.35s both' }}>
              <button
                onClick={handleNext}
                disabled={!state.rating}
                className={`w-full submit-btn py-3 sm:py-4 rounded-xl text-white font-semibold font-display text-sm sm:text-base tracking-wide
                  transition-all duration-300 relative overflow-hidden
                  ${!state.rating ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                aria-disabled={!state.rating}
              >
                <span className="relative z-10">
                  {state.rating
                    ? `Continue with "${selectedOption?.label}"`
                    : 'Select a rating to continue'}
                </span>
              </button>
            </div>
          </div>
        )}

        {state.step === 'comment' && selectedOption && (
          <div className="space-y-6" style={{ animation: 'slideUp 0.5s ease-out' }}>
            <div className="text-center space-y-2">
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border"
                style={{
                  borderColor: selectedOption.glowColor,
                  background: selectedOption.bgGradient,
                  boxShadow: `0 0 20px ${selectedOption.glowColor}40`,
                }}
              >
                <span className="text-xl">{selectedOption.emoji}</span>
                <span className={`font-display font-semibold text-sm ${selectedOption.color}`}>
                  {selectedOption.label}
                </span>
              </div>
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold font-display text-white mt-3">
                Want to share more?
              </h2>
              <p className="text-slate-400 text-sm">
                Optional — a few words help us serve you better
              </p>
            </div>

            <div style={{ animation: 'slideUp 0.5s ease-out 0.1s both' }}>
              <textarea
                className="survey-textarea w-full rounded-xl p-4 text-sm sm:text-base min-h-[120px] sm:min-h-[140px] font-body"
                placeholder="Tell us what you loved, or what we could improve…"
                value={state.comment}
                onChange={e => setState(prev => ({ ...prev, comment: e.target.value }))}
                maxLength={500}
                aria-label="Optional comment"
              />
              <div className="flex justify-between mt-1.5">
                <span className="text-xs text-slate-600">Max 500 characters</span>
                <span className={`text-xs font-mono ${state.comment.length > 450 ? 'text-amber-400' : 'text-slate-600'}`}>
                  {state.comment.length}/500
                </span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3" style={{ animation: 'slideUp 0.5s ease-out 0.2s both' }}>
              <button
                onClick={handleSkip}
                className="order-2 sm:order-1 flex-1 py-3 rounded-xl border border-white/10 text-slate-400 hover:text-slate-200 hover:border-white/20 font-display text-sm font-medium transition-all duration-200"
              >
                Skip
              </button>
              <button
                onClick={handleSubmit}
                className="order-1 sm:order-2 flex-[2] submit-btn py-3 rounded-xl text-white font-semibold font-display text-sm sm:text-base tracking-wide cursor-pointer"
              >
                <span className="relative z-10">Submit Feedback →</span>
              </button>
            </div>

            <button
              onClick={() => setState(prev => ({ ...prev, step: 'rating' }))}
              className="w-full text-center text-xs text-slate-600 hover:text-slate-400 transition-colors font-display"
            >
              ← Change my rating
            </button>
          </div>
        )}

        <div
          className="absolute bottom-0 left-8 right-8 h-px opacity-20"
          style={{ background: 'linear-gradient(90deg, transparent, #a78bfa, transparent)' }}
        />
      </div>

      <p className="mt-6 text-slate-700 text-xs font-display text-center">
        Your responses are anonymous · Thank you for your time
      </p>

      {/* ---- THANK YOU POPUP ---- */}
      {showPopup && selectedOption && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          style={{ animation: 'fadeIn 0.25s ease-out' }}
          onClick={handleClosePopup}
        >
          <div
            className="relative w-full max-w-sm glass-card rounded-2xl p-8 text-center shadow-2xl"
            style={{
              animation: 'slideUp 0.35s ease-out',
              boxShadow: `0 0 60px ${selectedOption.glowColor}40, 0 25px 50px rgba(0,0,0,0.5)`,
            }}
            onClick={e => e.stopPropagation()}
          >
            <div className="text-5xl mb-4">{selectedOption.emoji}</div>
            <h2 className="text-2xl font-bold font-display text-white mb-2">
              Thank you!
            </h2>
            <p className={`text-sm font-display font-semibold mb-1 ${selectedOption.color}`}>
              You rated us "{selectedOption.label}"
            </p>
            {state.comment && (
              <p className="text-slate-400 text-xs italic mb-3">
                "{state.comment}"
              </p>
            )}
            <p className="text-slate-400 text-sm mb-6">
              We appreciate you taking the time to share your feedback.
            </p>

            <button
              onClick={handleReset}
              className="submit-btn w-full py-3 rounded-xl text-white font-semibold font-display text-sm tracking-wide cursor-pointer"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;